import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import styles from './Login.module.css';

// Declare the google global
declare global {
  interface Window {
    google: any;
  }
}

const Login: React.FC = () => {
  const navigate = useNavigate();
  const { login, accessToken, isLoading: authLoading } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [gsiLoaded, setGsiLoaded] = useState(false);

  // Redirect if already logged in
  useEffect(() => {
    if (accessToken && !authLoading) {
      navigate('/');
    }
  }, [accessToken, authLoading, navigate]);

  // Handle the authorization code response
  const handleAuthResponse = useCallback(async (response: any) => {
    if (response.error) {
      console.error('Google login error:', response);
      setError(`Google login failed: ${response.error}`);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      console.log('Sending authorization code to backend');
      const apiResponse = await api.post('/api/auth/google/', {
        code: response.code
      });

      const { access, refresh, user } = apiResponse.data;
      console.log('Authentication successful');

      // Update auth context
      login(access, refresh, user);

      // Redirect to home page
      navigate('/');
    } catch (error: any) {
      console.error('Login failed:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Login failed. Please try again.';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [login, navigate]);

  // Initialize Google Identity Services
  useEffect(() => {
    const initializeGsi = () => {
      if (!window.google || gsiLoaded) return;

      const client = window.google.accounts.oauth2.initCodeClient({
        client_id: import.meta.env.VITE_GOOGLE_CLIENT_ID || '',
        scope: 'openid email profile https://www.googleapis.com/auth/gmail.send',
        callback: handleAuthResponse,
        access_type: 'offline',  // Request refresh token
        prompt: 'consent',       // Force consent to ensure refresh token
        ux_mode: 'popup',
      });

      // Store the client for use in the click handler
      (window as any).googleOAuthClient = client;
      setGsiLoaded(true);
    };

    // Check if GSI is already loaded
    if (window.google) {
      initializeGsi();
    } else {
      // Wait for GSI to load
      const checkGsiLoaded = setInterval(() => {
        if (window.google) {
          clearInterval(checkGsiLoaded);
          initializeGsi();
        }
      }, 100);

      // Cleanup
      return () => clearInterval(checkGsiLoaded);
    }
  }, [handleAuthResponse, gsiLoaded]);

  const handleGoogleLogin = () => {
    if (!gsiLoaded) {
      setError('Google Identity Services is still loading. Please try again.');
      return;
    }

    const client = (window as any).googleOAuthClient;
    if (client) {
      client.requestCode();
    } else {
      setError('Google login is not initialized. Please refresh the page.');
    }
  };

  return (
    <>
      {/* Full-page background */}
      <div style={{
        minHeight: '100vh',
        backgroundColor: 'var(--bg)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: "'Segoe UI', system-ui, Arial, sans-serif",
        position: 'relative',
        overflow: 'hidden',
      }}>

        {/* Decorative blurred orbs */}
        <div style={{
          position: 'absolute', top: '-80px', left: '-80px',
          width: '420px', height: '420px', borderRadius: '50%',
          background: 'radial-gradient(circle, color-mix(in srgb, var(--brand-green) 12%, transparent) 0%, transparent 70%)',
          pointerEvents: 'none',
        }} />
        <div style={{
          position: 'absolute', bottom: '-100px', right: '-60px',
          width: '500px', height: '500px', borderRadius: '50%',
          background: 'radial-gradient(circle, color-mix(in srgb, var(--brand-navy) 8%, transparent) 0%, transparent 70%)',
          pointerEvents: 'none',
        }} />

        {/* Floating decorative shapes */}
        <div style={{
          position: 'absolute', top: '12%', left: '8%',
          width: '56px', height: '56px', borderRadius: '14px',
          border: '2px solid color-mix(in srgb, var(--brand-green) 20%, transparent)',
          animation: 'oat-float 6s ease-in-out infinite',
          pointerEvents: 'none',
        }} />
        <div style={{
          position: 'absolute', bottom: '18%', right: '12%',
          width: '36px', height: '36px', borderRadius: '50%',
          border: '2px solid color-mix(in srgb, var(--brand-navy) 15%, transparent)',
          animation: 'oat-float2 5s ease-in-out infinite',
          pointerEvents: 'none',
        }} />
        <div style={{
          position: 'absolute', top: '60%', left: '6%',
          width: '24px', height: '24px', borderRadius: '6px',
          background: 'color-mix(in srgb, var(--brand-green) 12%, transparent)',
          animation: 'oat-float 7s ease-in-out infinite 1s',
          pointerEvents: 'none',
        }} />

        {/* Card */}
        <div className={styles.loginCard} style={{
          position: 'relative',
          width: '100%',
          maxWidth: '500px',
          background: 'var(--surface)',
          borderRadius: '24px',
          padding: '64px 56px 52px',
          textAlign: 'center',
          boxShadow: 'var(--shadow-pop)',
        }}>

          {/* Logo */}
          <img
            src="/oat-login-transparent.png"
            alt="OAT — Operation Automation Toolkit"
            width={751}
            height={369}
            style={{ width: '290px', height: 'auto', marginBottom: '8px' }}
          />

          {/* Divider */}
          <div style={{
            height: '1px',
            background: 'linear-gradient(90deg, transparent, var(--border), transparent)',
            margin: '20px 0 28px',
          }} />

          <p style={{
            fontSize: '14px',
            color: 'var(--text-dim)',
            margin: '0 0 28px',
            letterSpacing: '0.01em',
          }}>
            Sign in to your account to continue
          </p>

          {error && (
            <div style={{
              color: 'var(--neg)',
              fontSize: '13px',
              marginBottom: '20px',
              padding: '10px 14px',
              backgroundColor: 'var(--neg-soft)',
              borderRadius: '8px',
              border: '1px solid var(--neg-line)',
              textAlign: 'left',
            }}>
              {error}
            </div>
          )}

          {/* Google button */}
          <button
            className={styles.googleBtn}
            onClick={handleGoogleLogin}
            disabled={isLoading || !gsiLoaded}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px',
              backgroundColor: 'var(--surface)',
              border: '1.5px solid var(--border-strong)',
              borderRadius: '12px',
              color: 'var(--text)',
              cursor: isLoading || !gsiLoaded ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: 600,
              height: '48px',
              padding: '0 20px',
              opacity: isLoading || !gsiLoaded ? 0.55 : 1,
            }}
          >
            <svg version="1.1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" style={{ width: '18px', height: '18px', flexShrink: 0 }}>
              <g>
                <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
                <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
                <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
                <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
                <path fill="none" d="M0 0h48v48H0z" />
              </g>
            </svg>
            {isLoading ? 'Signing in...' : !gsiLoaded ? 'Loading...' : 'Continue with Google'}
          </button>

          <p style={{
            marginTop: '20px',
            fontSize: '11px',
            color: 'var(--text-faint)',
            lineHeight: 1.6,
          }}>
            By signing in, you&apos;ll be asked to grant Gmail permissions for order submissions
          </p>
        </div>

        {/* Footer */}
        <p style={{
          position: 'absolute',
          bottom: '20px',
          fontSize: '11px',
          color: 'var(--text-faint)',
          letterSpacing: '0.02em',
        }}>
          © {new Date().getFullYear()} Sameeksha Capital
        </p>
      </div>
    </>
  );
};

export default Login;